import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Megaphone, Palette, Search, ShieldAlert, X } from 'lucide-react'
import {
  useGetContentBoard,
  useGetContentCalendar,
  useGetDesignRequests,
  useGetMarketingRefs,
} from '../hooks/backend/marketing'
import { useCapabilities } from '../hooks/useCapabilities'
// Exactly one panel is mounted, never one of each hidden by CSS. See the
// comment where it is used: two mounted copies double every read and split the
// half-typed reason between two components.
import { useIsDesktop } from '../hooks/useIsDesktop'
import { useSignedInEmail } from '../auth/SignedInUser'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { Button } from '../lib/shadcn/button'
import { NativeSelect } from '../lib/shadcn/native-select'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { ContentBoard } from '../components/marketing/ContentBoard'
import { ContentCalendar, type CalendarDay } from '../components/marketing/ContentCalendar'
import { ContentPanel } from '../components/marketing/ContentPanel'
import { DesignRequestBoard } from '../components/marketing/DesignRequestBoard'
import { DesignRequestDialog } from '../components/marketing/DesignRequestDialog'
import { NewContentDialog } from '../components/marketing/NewContentDialog'
import { PlanDialog } from '../components/marketing/PlanDialog'
import { PlanUpload } from '../components/marketing/PlanUpload'
import { PlanReview } from '../components/marketing/PlanReview'
import {
  boardCounts,
  longestWait,
  monthLabel,
  waitedFor,
  type ContentRow,
  type DesignRequestRow,
  type MarketingPlan,
  type MarketingRefs,
} from '../utils/marketing'

type View = 'content' | 'calendar' | 'design' | 'plans'

const VIEWS: { value: View; label: string }[] = [
  { value: 'content', label: 'Content' },
  // Second, not last. The gap is the thing worth seeing first and a tab three
  // clicks in is a tab nobody opens.
  { value: 'calendar', label: 'Calendar' },
  { value: 'design', label: 'Design queue' },
  { value: 'plans', label: 'Plans' },
]

export default function Marketing() {
  const { loading: accessLoading, canView, caption } = useCapabilities()
  const signedInEmail = useSignedInEmail()
  const isDesktop = useIsDesktop()

  const [view, setView] = useState<View>('content')
  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [onlyAwaiting, setOnlyAwaiting] = useState(false)
  const [audience, setAudience] = useState('')
  const [includeFinished, setIncludeFinished] = useState(false)
  const [openItemId, setOpenItemId] = useState<string | null>(null)
  // Which design request is expanded. Held here rather than inside
  // DesignRequestBoard so a notification can open one. Migration 0152.
  const [openDesignId, setOpenDesignId] = useState<string | null>(null)
  // A plan upload open for review. It takes the whole Plans view, because the
  // review is twenty posts wide and does not fit beside the list.
  const [openImportId, setOpenImportId] = useState<string | null>(null)
  const [newContentOpen, setNewContentOpen] = useState(false)
  const [planDialog, setPlanDialog] = useState<{ open: boolean; plan: MarketingPlan | null }>({
    open: false,
    plan: null,
  })
  // Two ways in to the same dialog: the header button asks for a design about
  // nothing in particular, and the panel's button asks for one about the item
  // that is open. Kept as two pieces of state rather than one nullable, because
  // "closed" and "open with no item" are different states and collapsing them
  // makes the header button impossible to express.
  const [designFor, setDesignFor] = useState<ContentRow | null>(null)
  const [designOpen, setDesignOpen] = useState(false)

  // A notification links to the thing itself, not to the board, so /marketing
  // takes ?item=<content id> or ?design=<request id>.
  //
  // APPLIED ONCE, and the ref is the whole point: without it, closing the panel
  // and letting any state change re-run this effect would drag it straight back
  // open, which is the shape of bug that makes a screen feel haunted.
  const [searchParams, setSearchParams] = useSearchParams()
  const deepLinkApplied = useRef(false)
  useEffect(() => {
    if (deepLinkApplied.current) return
    const item = searchParams.get('item')
    const design = searchParams.get('design')
    if (!item && !design) return
    deepLinkApplied.current = true
    if (item) {
      setView('content')
      setOpenItemId(item)
      // A linked item is very often Approved or Archived, and the board hides
      // finished work by default, so the row the panel is about would not be in
      // the list behind it. Turned on rather than leaving a panel floating over
      // a board that does not contain the thing it describes.
      setIncludeFinished(true)
    } else if (design) {
      setView('design')
      setOpenDesignId(design)
    }
    // Cleared afterwards so a refresh does not reopen what somebody just closed,
    // and so the address bar stops carrying a uuid that means nothing to anyone.
    const next = new URLSearchParams(searchParams)
    next.delete('item')
    next.delete('design')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const refsQuery = useGetMarketingRefs()
  const board = useGetContentBoard()
  const calendar = useGetContentCalendar<CalendarDay[]>()
  const designs = useGetDesignRequests()

  const refsTrigger = refsQuery.trigger
  const boardTrigger = board.trigger
  const designsTrigger = designs.trigger

  // Filtered server-side, so the counts on this screen are counts of what was
  // asked for and not of a page-sized slice of it. Same call as the task board.
  const load = useCallback(() => {
    void boardTrigger(
      {
        planId: planFilter || null,
        search: search.trim() || undefined,
        onlyAwaiting,
        includeFinished,
        audiences: audience ? [audience] : undefined,
      },
      { skipCache: true },
    )
  }, [boardTrigger, planFilter, search, onlyAwaiting, includeFinished, audience])

  useEffect(() => {
    if (canView) void refsTrigger({}, { skipCache: true })
  }, [canView, refsTrigger])

  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  useEffect(() => {
    if (canView) void designsTrigger({}, { skipCache: true })
  }, [canView, designsTrigger])

  const refs = (refsQuery.data as MarketingRefs | null | undefined) ?? null
  const rows = (board.data as ContentRow[] | undefined) ?? []
  const designRows = (designs.data as DesignRequestRow[] | undefined) ?? []

  // From the server, not from the capabilities hook, so the buttons and the
  // refusals behind them come from one answer.
  const canEditMarketing = refs?.canEditMarketing === true
  const canApproveContent = refs?.canApproveContent === true

  const counts = useMemo(() => boardCounts(rows), [rows])
  const waiting = useMemo(() => longestWait(rows), [rows])

  const calendarTrigger = calendar.trigger
  const reloadAll = useCallback(() => {
    load()
    void designsTrigger({}, { skipCache: true })
    void refsTrigger({}, { skipCache: true })
    // The calendar is refreshed with everything else rather than only when its
    // own tab is opened. A post scheduled on the content board changes which
    // days are empty, and a calendar that only catches up when you click it is
    // a calendar that tells you yesterday's gaps.
    void calendarTrigger({}, { skipCache: true })
  }, [load, designsTrigger, refsTrigger, calendarTrigger])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  const panel =
    openItemId === null ? null : (
      <ContentPanel
        itemId={openItemId}
        channels={refs?.channels ?? []}
        formats={refs?.formats ?? []}
        audiences={refs?.audiences ?? []}
        ctas={refs?.ctas ?? []}
        plans={refs?.plans ?? []}
        canEditMarketing={canEditMarketing}
        canApproveContent={canApproveContent}
        onClose={() => setOpenItemId(null)}
        onChanged={reloadAll}
        onRequestDesign={(item) => {
          setDesignFor(item)
          setDesignOpen(false)
        }}
      />
    )

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Marketing is available to signed-in Fleetly staff.
          </p>
          <p className="text-xs text-muted-foreground">
            Ask an admin to add you to cockpit access if you should be able to see the content plan.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="border-b border-border px-4 py-3">
        {/* Wraps below sm, and the actions take their own full-width row there.
            Found by rendering at 430px: with everything on one line the search
            box pushed New content off the right edge, where it could not be
            reached at all. Three of the six people who use this work from a
            phone. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <Megaphone className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Marketing</h1>
          <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
            <div className="relative min-w-0 flex-1 sm:flex-none">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search content"
                className="h-8 w-full pl-7 text-sm sm:w-52"
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              onClick={() => {
                setDesignFor(null)
                setDesignOpen(true)
              }}
            >
              <Palette className="h-3.5 w-3.5" />
              Ask for a design
            </Button>
            {canEditMarketing && (
              <Button size="sm" className="h-8" onClick={() => setNewContentOpen(true)}>
                New content
              </Button>
            )}
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-border bg-background p-0.5">
            {VIEWS.map((v) => (
              <button
                key={v.value}
                type="button"
                onClick={() => setView(v.value)}
                className={cn(
                  'rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
                  view === v.value
                    ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {v.label}
              </button>
            ))}
          </div>

          {view === 'content' && (
            <>
              <button type="button" onClick={() => setOnlyAwaiting((o) => !o)}>
                <Chip tone={onlyAwaiting ? 'red' : counts.awaiting > 0 ? 'amber' : 'neutral'}>
                  {counts.awaiting}
                  <span className="ml-1 opacity-70">waiting on approval</span>
                </Chip>
              </button>
              {/* The longest wait, named. A count of things waiting does not say
                  whether the queue is a day old or a fortnight old, and the
                  second is the one worth interrupting somebody about. */}
              {waiting !== null && waiting > 0 && (
                <Chip tone={waiting >= 3 ? 'red' : 'amber'}>
                  longest wait {waitedFor(waiting)}
                </Chip>
              )}
              {counts.overdue > 0 && (
                <Chip tone="red">
                  {counts.overdue}
                  <span className="ml-1 opacity-70">approved but the date has passed</span>
                </Chip>
              )}
              {counts.noFile > 0 && (
                <Chip tone="amber">
                  {counts.noFile}
                  <span className="ml-1 opacity-70">submitted with no file</span>
                </Chip>
              )}
              {counts.notChosen > 0 && (
                <Chip tone="amber">
                  {counts.notChosen}
                  <span className="ml-1 opacity-70">with no caption chosen</span>
                </Chip>
              )}
              {/* Not an error. A second look, counted so it can be planned
                  rather than discovered one row at a time. */}
              {counts.flagged > 0 && (
                <Chip tone="amber">
                  {counts.flagged}
                  <span className="ml-1 opacity-70">needing a guardrail check</span>
                </Chip>
              )}
              {counts.noSource > 0 && (
                <Chip tone="amber">
                  {counts.noSource}
                  <span className="ml-1 opacity-70">with a figure and no source</span>
                </Chip>
              )}
              {/* Named plainly rather than hidden. Content with no date is not
                  scheduled, it is unmeasured. */}
              <Chip tone={counts.noDate > 0 ? 'amber' : 'neutral'}>
                {counts.noDate}
                <span className="ml-1 opacity-70">with no date set</span>
              </Chip>
              <Chip tone="neutral">
                {counts.total}
                <span className="ml-1 opacity-70">shown</span>
              </Chip>

              <NativeSelect
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="h-7 w-44 text-xs"
              >
                <option value="">Every plan</option>
                {(refs?.plans ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </NativeSelect>

              <NativeSelect
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="h-7 w-40 text-xs"
              >
                <option value="">Every audience</option>
                {(refs?.audiences ?? []).map((a) => (
                  <option key={a.code} value={a.code}>
                    {a.label}
                  </option>
                ))}
              </NativeSelect>

              <button
                type="button"
                onClick={() => setIncludeFinished((f) => !f)}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                {includeFinished ? 'Hide published and archived' : 'Show published and archived'}
              </button>

              {(planFilter || onlyAwaiting || search || audience) && (
                <button
                  type="button"
                  onClick={() => {
                    setPlanFilter('')
                    setOnlyAwaiting(false)
                    setSearch('')
                    setAudience('')
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" /> Clear filters
                </button>
              )}
            </>
          )}

          {view === 'plans' && canEditMarketing && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => setPlanDialog({ open: true, plan: null })}
            >
              New plan
            </Button>
          )}
        </div>
      </div>

      {/* THE FINDING THIS FEATURE EXISTS FOR, said on the way in.
          A plan states the mix it wants and then a month runs; nobody counts
          twenty headings in a document to check. Shown only when one plan is
          selected, because a mix is a property of one plan and summing two of
          them says nothing. */}
      {view === 'content' &&
        planFilter &&
        (() => {
          const plan = (refs?.plans ?? []).find((p) => p.id === planFilter)
          if (!plan || !plan.mix_off_target) return null
          return (
            <div className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <span className="font-medium">{plan.title} is not the mix it plans for.</span>{' '}
              {plan.mix_summary}
              {plan.feed_size ? `, against a ${plan.feed_size} post feed` : ''}.
            </div>
          )
        })()}

      {board.error && view === 'content' && (
        <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">
          {board.error}
        </div>
      )}

      <div className="flex min-h-0 flex-1 lg:flex-row">
        <div className="min-h-0 min-w-0 flex-1 overflow-auto">
          {view === 'content' && (
            <>
              {board.loading && rows.length === 0 && (
                <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
                  <Spinner className="h-4 w-4" /> Loading the board…
                </div>
              )}
              {!board.loading && rows.length === 0 && (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  {search || planFilter || onlyAwaiting
                    ? 'Nothing matches. Clear the filters.'
                    : 'Nothing on the board yet. Add the first piece of content.'}
                </div>
              )}
              <ContentBoard rows={rows} selectedId={openItemId} onOpen={setOpenItemId} />
            </>
          )}

          {view === 'calendar' && (
            <div className="p-4">
              <ContentCalendar
                days={(calendar.data as CalendarDay[] | null) ?? null}
                loading={calendar.loading}
                error={calendar.error ? String(calendar.error) : null}
                onOpenItem={(id) => {
                  setOpenItemId(id)
                  setView('content')
                }}
                onReload={() => {
                  void calendar.trigger({})
                }}
              />
            </div>
          )}

          {view === 'design' && (
            <DesignRequestBoard
              rows={designRows}
              contentItems={rows}
              signedInEmail={signedInEmail}
              canAttachToContent={canEditMarketing}
              isDesigner={refs?.isDesigner === true}
              onChanged={reloadAll}
              openId={openDesignId}
              onOpen={setOpenDesignId}
            />
          )}

          {view === 'plans' && openImportId && (
            <PlanReview
              importId={openImportId}
              audiences={refs?.audiences ?? []}
              canEdit={canEditMarketing}
              onBack={() => setOpenImportId(null)}
              onCommitted={reloadAll}
            />
          )}
          {view === 'plans' && !openImportId && canEditMarketing && <PlanUpload onOpen={setOpenImportId} />}
          {view === 'plans' && !openImportId && (
            <ul className="divide-y divide-border">
              {(refs?.plans ?? []).length === 0 && (
                <li className="p-8 text-center text-sm text-muted-foreground">
                  No plans yet. A plan is one month of intent, with a name.
                </li>
              )}
              {(refs?.plans ?? []).map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-2 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{p.title}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {monthLabel(p.period_month)}
                      {p.feed_size ? `, ${p.feed_size} posts planned` : ''}
                      {p.objective ? `, ${p.objective}` : ''}
                    </p>
                    {/* Posts, not percentages. "Driver 17 of 16 planned" needs no
                        interpretation and a percentage gap cannot even express a
                        one-post error on a twenty-post feed. */}
                    {p.mix_summary && (
                      <p
                        className={
                          p.mix_off_target
                            ? 'mt-0.5 text-[11px] text-amber-700 dark:text-amber-300'
                            : 'mt-0.5 text-[11px] text-muted-foreground'
                        }
                      >
                        {p.mix_summary}
                      </p>
                    )}
                  </div>
                  <Chip tone={p.status === 'Active' ? 'brand' : 'neutral'}>{p.status}</Chip>
                  {p.mix_off_target && <Chip tone="amber">mix off target</Chip>}
                  <Chip tone="neutral">
                    {p.item_count}
                    <span className="ml-1 opacity-70">items</span>
                  </Chip>
                  {p.awaiting_count > 0 && (
                    <Chip tone="amber">
                      {p.awaiting_count}
                      <span className="ml-1 opacity-70">waiting</span>
                    </Chip>
                  )}
                  {/* Null, not zero, for a plan with nothing in it. An empty plan
                      has no completion to report and "0 per cent published"
                      reads as a failure rather than as an absence. */}
                  <Chip tone={p.published_pct === null ? 'neutral' : 'green'}>
                    {p.published_pct === null ? 'nothing to measure' : `${p.published_pct}% out`}
                  </Chip>
                  {canEditMarketing && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() => setPlanDialog({ open: true, plan: p })}
                    >
                      Edit
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ONE PANEL, chosen by a media query, never one of each hidden with
            `lg:block` and `lg:hidden`. Two mounted copies were the first version
            of this and rendering it showed both costs: getContentItem fired
            twice per open, and a reason half typed into the desktop copy was not
            in the phone copy, so crossing the breakpoint mid-review lost it
            silently. useIsDesktop exists for exactly this. */}
        {openItemId && view === 'content' && isDesktop && (
          <div className="w-[30rem] shrink-0 border-l border-border">{panel}</div>
        )}
      </div>

      {/* Below lg the panel takes the whole screen rather than squeezing into a
          column too narrow to read a review trail in. Three of the six people
          who use this work from a phone. */}
      {openItemId && view === 'content' && !isDesktop && (
        <div className="fixed inset-0 z-40 overflow-hidden bg-card">{panel}</div>
      )}

      <NewContentDialog
        open={newContentOpen}
        onOpenChange={setNewContentOpen}
        channels={refs?.channels ?? []}
        formats={refs?.formats ?? []}
        audiences={refs?.audiences ?? []}
        ctas={refs?.ctas ?? []}
        plans={refs?.plans ?? []}
        people={(refs?.people ?? []).filter((p) => p.can_edit_marketing)}
        defaultPlanId={planFilter || null}
        onCreated={(id) => {
          reloadAll()
          setOpenItemId(id)
        }}
      />

      <PlanDialog
        open={planDialog.open}
        plan={planDialog.plan}
        audiences={refs?.audiences ?? []}
        onOpenChange={(o) => setPlanDialog((d) => ({ open: o, plan: o ? d.plan : null }))}
        onSaved={reloadAll}
      />

      <DesignRequestDialog
        open={designOpen || designFor !== null}
        onOpenChange={(o) => {
          if (o) return
          setDesignOpen(false)
          setDesignFor(null)
        }}
        contentItemId={designFor?.id ?? null}
        contentItemTitle={designFor?.title ?? null}
        onCreated={reloadAll}
      />
    </div>
  )
}
